import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import Logger from "../../services/Logger";
import StatsJigsawService from "../../services/StatsJigsawService";
import StatsSlidingService from "../../services/StatsSlidingService";

function overlapInTopFive(rows) {
    if (!Array.isArray(rows)) return 0;
    return rows.slice(0, 5).filter((row) => row?.editorsPicks === true).length;
}

async function loadEffectiveCount(topPuzzles) {
    const [selectedRes, topFiveRes] = await Promise.all([
        topPuzzles({
            editorsPicksOnly: true,
            page: 0,
            pageSize: 1,
            includeTest: false,
            excludeStaff: true,
            sortField: "completedPlays",
            sortDir: "desc",
        }),
        topPuzzles({
            editorsPicksOnly: false,
            page: 0,
            pageSize: 5,
            includeTest: false,
            excludeStaff: true,
            sortField: "completedPlays",
            sortDir: "desc",
        }),
    ]);
    const selected = Number(selectedRes?.total) || 0;
    const overlap = overlapInTopFive(topFiveRes?.puzzles);
    return {
        selected,
        overlap,
        count: Math.max(0, selected - overlap),
    };
}

function EditorsPickCard({ title, href, stats, loading, error }) {
    const count = stats?.count ?? 0;
    const warn = !loading && !error && count <= 5;
    return (
        <Card
            sx={{
                flex: "1 1 280px",
                minWidth: 0,
                border: "2px solid",
                borderColor: warn ? "warning.main" : "divider",
                bgcolor: warn ? "warning.light" : "background.paper",
            }}
        >
            <CardActionArea
                component={RouterLink}
                to={href}
                sx={{ height: "100%", alignItems: "stretch" }}
            >
                <CardContent sx={{ minHeight: 160 }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                        {title}
                    </Typography>
                    {loading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
                            <CircularProgress size={22} />
                            <Typography variant="body2" color="text.secondary">
                                Loading…
                            </Typography>
                        </Box>
                    ) : error ? (
                        <Typography variant="body2" color="error">
                            Could not load editor&apos;s picks.
                        </Typography>
                    ) : (
                        <>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: "bold",
                                    color: warn ? "warning.dark" : "text.primary",
                                }}
                            >
                                {count}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                Selected picks not already in Top 5
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                {stats?.selected ?? 0} selected − {stats?.overlap ?? 0} also in Top 5
                                {warn ? " — add more picks" : ""}
                            </Typography>
                        </>
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    );
}

/** Editor's-pick counts on /admin: selected minus those already ranked in completed Top 5. */
export default function EditorsPicksOverview() {
    const [jigsaw, setJigsaw] = useState(null);
    const [sliding, setSliding] = useState(null);
    const [loading, setLoading] = useState(true);
    const [jigsawError, setJigsawError] = useState(false);
    const [slidingError, setSlidingError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            const [jigsawResult, slidingResult] = await Promise.allSettled([
                loadEffectiveCount((opts) => StatsJigsawService.topPuzzles(opts)),
                loadEffectiveCount((opts) => StatsSlidingService.topPuzzles(opts)),
            ]);
            if (cancelled) return;
            if (jigsawResult.status === "fulfilled") {
                setJigsaw(jigsawResult.value);
                setJigsawError(false);
            } else {
                Logger.error("Error loading jigsaw editor's picks:", jigsawResult.reason);
                setJigsawError(true);
            }
            if (slidingResult.status === "fulfilled") {
                setSliding(slidingResult.value);
                setSlidingError(false);
            } else {
                Logger.error("Error loading sliding editor's picks:", slidingResult.reason);
                setSlidingError(true);
            }
            setLoading(false);
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <Box sx={{ mb: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                Editor&apos;s picks
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Count = selected puzzles minus any that are already in the completed Top 5. Warning when the
                remaining count is 5 or fewer.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <EditorsPickCard
                    title="Jigsaw"
                    href="/admin/jigsaw/stats"
                    stats={jigsaw}
                    loading={loading}
                    error={jigsawError}
                />
                <EditorsPickCard
                    title="Sliding"
                    href="/admin/sliding/stats"
                    stats={sliding}
                    loading={loading}
                    error={slidingError}
                />
            </Box>
        </Box>
    );
}
